import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import Toast from "../../components/Common/Toast";
import commonService, { normalizeListResponse } from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { formatIST } from "../../utils/dateHelpers";
import "../../assets/styles/admin-dashboard.css";
import {
  Layers3,
  CheckCircle2,
  Clock3,
  Activity,
  TrendingUp,
  Users,
  RefreshCcw,
  CalendarRange,
} from "lucide-react";

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState<any>(null);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [createdBy, setCreatedBy] = useState("");

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const fetchAll = async () => {
    setLoading(true);

    try {
      const [dashboardRes, usersRes] = await Promise.all([
        commonService.dashboard({
          start_date: startDate,
          end_date: endDate,
          created_by: createdBy || undefined,
        }),
        commonService.getUsers({ limit: 1 }).catch(() => null),
      ]);

      setDashboard(dashboardRes.data);

      if (usersRes) {
        setTotalUsers(normalizeListResponse(usersRes).total);
      }
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load dashboard") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = dashboard?.campaign_summary || {};

  return (
    <AdminLayout>
      <div className="admin-dashboard-content">
        <div
          style={{
            background: "linear-gradient(135deg, #4338ca 0%, #0891b2 100%)",
            borderRadius: "24px",
            padding: "30px",
            color: "white",
            marginBottom: "24px",
            boxShadow: "0 20px 45px rgba(67,56,202,0.25)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <div>
              <div style={{ fontSize: "30px", fontWeight: 800, marginBottom: "8px" }}>
                Admin Dashboard
              </div>
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px" }}>
                Cross-account visibility into every campaign, user and event.
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              <div style={filterBoxStyle}>
                <CalendarRange size={16} />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={filterInputStyle}
                />
              </div>

              <div style={filterBoxStyle}>
                <CalendarRange size={16} />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={filterInputStyle}
                />
              </div>

              <input
                placeholder="Filter by creator email"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                style={{ ...filterBoxStyle, outline: "none", width: "190px" }}
              />

              <button onClick={fetchAll} style={refreshBtnStyle}>
                <RefreshCcw size={16} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ background: "white", borderRadius: "20px", padding: "60px", textAlign: "center" }}>
            Loading dashboard...
          </div>
        ) : (
          <>
            <div className="admin-dashboard-stats-grid">
              <MetricCard
                title="Total Users"
                value={totalUsers ?? "-"}
                subtitle="Registered accounts"
                color="#0891b2"
                icon={<Users size={24} />}
                onClick={() => navigate("/admin/users")}
              />
              <MetricCard
                title="Total Campaigns"
                value={summary.total_campaigns || 0}
                subtitle="Across all users"
                color="#4338ca"
                icon={<Layers3 size={24} />}
                onClick={() => navigate("/admin/campaigns")}
              />
              <MetricCard
                title="Closed"
                value={summary.closed || 0}
                subtitle="Successfully completed"
                color="#16a34a"
                icon={<CheckCircle2 size={24} />}
              />
              <MetricCard
                title="Pending"
                value={summary.open || 0}
                subtitle="Awaiting action"
                color="#f59e0b"
                icon={<Clock3 size={24} />}
              />
              <MetricCard
                title="In Progress"
                value={summary.inprogress || 0}
                subtitle="Currently processing"
                color="#2563eb"
                icon={<Activity size={24} />}
              />
              <MetricCard
                title="Ready"
                value={summary.ready || 0}
                subtitle="Ready to launch"
                color="#7c3aed"
                icon={<TrendingUp size={24} />}
              />
            </div>

            <div className="admin-dashboard-main-grid">
              <div style={panelStyle}>
                <div style={panelTitleStyle}>Team Performance</div>

                {!dashboard?.user_summary || dashboard.user_summary.length === 0 ? (
                  <div style={{ color: "#9ca3af", fontSize: "13px", marginTop: "12px" }}>
                    No user activity yet
                  </div>
                ) : (
                  dashboard.user_summary.map((u: any, i: number) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "14px 0",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, color: "#111827", marginBottom: "4px" }}>
                          {u.created_by}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          {u.lead_count} leads generated
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#eef2ff",
                          color: "#4338ca",
                          padding: "7px 13px",
                          borderRadius: "999px",
                          fontWeight: 700,
                          fontSize: "12px",
                        }}
                      >
                        {u.campaign_count}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div style={panelStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <div style={panelTitleStyle}>Recent Campaigns</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>Across every user</div>
                </div>

                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                  <table style={{ width: "100%", minWidth: "560px", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Campaign</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Leads</th>
                        <th style={thStyle}>Created By</th>
                        <th style={thStyle}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dashboard?.recent_campaigns || []).map((item: any) => (
                        <tr
                          key={item.id}
                          onClick={() => navigate("/admin/campaigns")}
                          style={{ cursor: "pointer" }}
                        >
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 600, color: "#111827" }}>{item.file_name}</div>
                          </td>
                          <td style={tdStyle}>
                            <span
                              style={{
                                padding: "5px 11px",
                                borderRadius: "999px",
                                fontSize: "11px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                background:
                                  item.status === "ready"
                                    ? "#dcfce7"
                                    : item.status === "open"
                                      ? "#fee2e2"
                                      : item.status === "completed"
                                        ? "#d1fae5"
                                        : "#dbeafe",
                                color:
                                  item.status === "ready"
                                    ? "#166534"
                                    : item.status === "open"
                                      ? "#991b1b"
                                      : item.status === "completed"
                                        ? "#065f46"
                                        : "#1d4ed8",
                              }}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td style={tdStyle}>{item.data_count}</td>
                          <td style={tdStyle}>{item.created_by}</td>
                          <td style={tdStyle}>
                            {item.created_at ? formatIST(item.created_at, { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

const MetricCard: React.FC<{
  title: string;
  value: React.ReactNode;
  subtitle: string;
  color: string;
  icon: React.ReactNode;
  onClick?: () => void;
}> = ({ title, value, subtitle, color, icon, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: "white",
      borderRadius: "18px",
      padding: "20px",
      border: "1px solid #e5e7eb",
      boxShadow: "0 10px 25px rgba(0,0,0,0.04)",
      position: "relative",
      overflow: "hidden",
      cursor: onClick ? "pointer" : "default",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: "-25px",
        right: "-25px",
        width: "90px",
        height: "90px",
        background: color,
        opacity: 0.08,
        borderRadius: "50%",
      }}
    />
    <div>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", marginBottom: "8px" }}>
        {title}
      </div>
      <div style={{ fontSize: "30px", fontWeight: 800, color: "#111827", lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ marginTop: "8px", fontSize: "11px", color: "#9ca3af" }}>{subtitle}</div>
    </div>
    <div
      style={{
        width: "50px",
        height: "50px",
        borderRadius: "14px",
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
      }}
    >
      {icon}
    </div>
  </div>
);

const panelStyle: React.CSSProperties = {
  background: "white",
  borderRadius: "20px",
  padding: "22px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
  // Grid items default to min-width:auto, which refuses to shrink below
  // the widest descendant (the Recent Campaigns table's minWidth:560px) -
  // that silently grows this panel past the viewport on mobile instead of
  // letting the table's own overflowX:auto wrapper handle the overflow
  // locally. minWidth:0 lets the grid track actually constrain this panel
  // to the available width so the inner scroll container can do its job.
  minWidth: 0,
};

const panelTitleStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#111827",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 10px",
  fontSize: "11px",
  color: "#6b7280",
  borderBottom: "1px solid #e5e7eb",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 10px",
  fontSize: "13px",
  color: "#374151",
  borderBottom: "1px solid #f9fafb",
};

const filterBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.18)",
  padding: "10px 14px",
  borderRadius: "12px",
  color: "white",
};

const filterInputStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  outline: "none",
  color: "white",
  fontSize: "13px",
};

const refreshBtnStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: "white",
  color: "#4338ca",
  border: "none",
  borderRadius: "12px",
  padding: "11px 16px",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: "13px",
};

export default AdminDashboardPage;
